import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import gamesData from '../../data/games.json';
import SportsGameCard from './SportsGameCard';
import SportsConsentBanner from './SportsConsentBanner';

const SportsGames = () => {
  // Transform and initialize games immediately - no loading delay
  const transformedGames = useMemo(() => {
    return gamesData.map((game, index) => ({
      id: index + 1,
      title: game.name,
      thumbnail: game.thumb4 || game.thumb3 || game.thumb2 || game.thumb1 || '/api/placeholder/300/200',
      genre: game.category,
      platform: 'Web Browser',
      releaseDate: game.create_date,
      gameUrl: game.url,
      shortDescription: game.description,
      embeddable: true,
      thumbnails: {
        small: game.thumb1,
        medium: game.thumb2,
        large: game.thumb3,
        extra: [game.thumb4, game.thumb5, game.thumb6, game.thumb7, game.thumb8]
      }
    }));
  }, []);

  // Initialize games directly - no loading state needed
  const [games] = useState(transformedGames);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedGame, setSelectedGame] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  
  // **ENHANCED: Mobile landscape gaming states**
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [orientation, setOrientation] = useState('portrait');
  const [isMobile, setIsMobile] = useState(false);
  const [isLandscapeGaming, setIsLandscapeGaming] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  // **NEW: Enhanced mobile detection and orientation handling**
  useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const mobile = width <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      setWindowSize({ width, height });
      setIsMobile(mobile);
      
      // Determine orientation more accurately
      let newOrientation;
      if (window.screen && window.screen.orientation) {
        newOrientation = window.screen.orientation.type;
      } else {
        newOrientation = width > height ? 'landscape-primary' : 'portrait-primary';
      }
      
      setOrientation(newOrientation);
      
      // **KEY FIX: Auto-enable landscape gaming mode on mobile when playing**
      const isLandscape = width > height || newOrientation.includes('landscape');
      if (isPlaying && mobile && isLandscape) {
        setIsLandscapeGaming(true);
        // Auto-enter fullscreen on mobile landscape
        if (!isFullscreen) {
          enterFullscreenMode();
        }
      } else if (!isPlaying || !mobile || !isLandscape) {
        setIsLandscapeGaming(false);
      }
    };

    const handleOrientationChange = () => {
      // Small delay to ensure dimensions are updated
      setTimeout(updateDeviceInfo, 100);
    };

    const handleFullscreenChange = () => {
      const isFS = !!(
        document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isFS);
      
      // If exiting fullscreen on mobile landscape while playing, show minimal controls
      if (!isFS && isPlaying && isMobile && orientation.includes('landscape')) {
        setIsLandscapeGaming(true);
      }
    };

    // Listen for all relevant events
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Initial setup
    updateDeviceInfo();

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isPlaying, isMobile, orientation, isFullscreen]);

  // **ENHANCED: Fullscreen management functions**
  const enterFullscreenMode = useCallback(() => {
    const gameContainer = document.getElementById('game-fullscreen-container');
    if (gameContainer) {
      if (gameContainer.requestFullscreen) {
        gameContainer.requestFullscreen();
      } else if (gameContainer.webkitRequestFullscreen) {
        gameContainer.webkitRequestFullscreen();
      } else if (gameContainer.mozRequestFullScreen) {
        gameContainer.mozRequestFullScreen();
      } else if (gameContainer.msRequestFullscreen) {
        gameContainer.msRequestFullscreen();
      }
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  }, []);

  // Load favorites from localStorage on component mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('game-favorites');
    if (savedFavorites) {
      try {
        const parsedFavorites = JSON.parse(savedFavorites);
        setFavorites(Array.isArray(parsedFavorites) ? parsedFavorites : []);
      } catch (error) {
        console.error('Error loading favorites from localStorage:', error);
        setFavorites([]);
      }
    }
  }, []);

  // Save favorites to localStorage whenever favorites change
  useEffect(() => {
    try {
      localStorage.setItem('game-favorites', JSON.stringify(favorites));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  }, [favorites]);

  // Sync dropdown with favorites view
  useEffect(() => {
    if (showFavorites && selectedCategory !== 'favorites') {
      setSelectedCategory('favorites');
    } else if (!showFavorites && selectedCategory === 'favorites') {
      setSelectedCategory('all');
    }
  }, [showFavorites, selectedCategory]);

  // Debounce search term to prevent excessive filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Check for EU users and show consent banner if needed
  useEffect(() => {
    const hasConsent = localStorage.getItem('user-consent');
    if (!hasConsent) {
      setShowConsentBanner(true);
    }
  }, []);

  // Handle consent acceptance
  const handleConsentAccept = useCallback(() => {
    localStorage.setItem('user-consent', 'accepted');
    setShowConsentBanner(false);
  }, []);

  // Handle consent decline
  const handleConsentDecline = useCallback(() => {
    localStorage.setItem('user-consent', 'declined');
    setShowConsentBanner(false);
  }, []);

  // Add to favorites function with success feedback
  const addToFavorites = useCallback((gameId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(gameId) 
        ? prev.filter(id => id !== gameId)
        : [...prev, gameId];
      
      const game = games.find(g => g.id === gameId);
      if (game) {
        const action = prev.includes(gameId) ? 'removed from' : 'added to';
        console.log(`${game.title} ${action} favorites`);
      }
      
      return newFavorites;
    });
  }, [games]);

  // Toggle favorites view
  const toggleFavoritesView = useCallback(() => {
    setShowFavorites(prev => {
      const newShowFavorites = !prev;
      if (newShowFavorites) {
        setSelectedCategory('favorites');
      } else {
        setSelectedCategory('all');
      }
      return newShowFavorites;
    });
  }, []);

  // Memoized categories
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(gamesData.map(game => game.category))];
    return [
      { value: 'all', label: 'All Games' },
      { value: 'favorites', label: `Favourite Games (${favorites.length})` },
      ...uniqueCategories.map(cat => ({
        value: cat.toLowerCase().replace(/\s+/g, '-'),
        label: cat
      }))
    ];
  }, [favorites.length]);

  // Optimized filtering with useMemo
  const filteredGames = useMemo(() => {
    let filtered = games;

    if (selectedCategory === 'favorites' || showFavorites) {
      filtered = filtered.filter(game => favorites.includes(game.id));
    }
    else if (selectedCategory !== 'all') {
      const categoryName = categories.find(cat => cat.value === selectedCategory)?.label;
      if (categoryName) {
        filtered = filtered.filter(game => game.genre === categoryName);
      }
    }

    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(game => 
        game.title.toLowerCase().includes(searchLower) ||
        game.genre.toLowerCase().includes(searchLower) ||
        game.shortDescription.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [games, selectedCategory, debouncedSearchTerm, categories, showFavorites, favorites]);

  // Reload games function
  const loadGames = useCallback(() => {
    setError(null);
  }, []);

  // Handle game click
  const handleGameClick = useCallback((game) => {
    if (game.gameUrl) {
      setSelectedGame(game);
      setIsPlaying(true);
      
      // **NEW: Auto-enable landscape gaming on mobile**
      if (isMobile) {
        const isLandscape = windowSize.width > windowSize.height || orientation.includes('landscape');
        if (isLandscape) {
          setIsLandscapeGaming(true);
          // Auto-enter fullscreen after a brief delay
          setTimeout(() => {
            enterFullscreenMode();
          }, 500);
        }
      }
    } else {
      alert(`Game ${game.title} is currently unavailable.`);
    }
  }, [isMobile, windowSize, orientation, enterFullscreenMode]);

  // Handle category change
  const handleCategoryChange = useCallback((e) => {
    const newCategory = e.target.value;
    setSelectedCategory(newCategory);
    
    if (newCategory === 'favorites') {
      setShowFavorites(true);
    } else {
      setShowFavorites(false);
    }
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // GameCard extracted to SportsGameCard.js
  const GameCard = ({ game }) => <SportsGameCard game={game} favorites={favorites} handleGameClick={handleGameClick} addToFavorites={addToFavorites} />;

  // **ENHANCED: Close game function with landscape gaming reset**
  const closeGame = useCallback(() => {
    setIsPlaying(false);
    setSelectedGame(null);
    setIsLandscapeGaming(false);
    
    if (isFullscreen) {
      exitFullscreen();
    }
  }, [isFullscreen, exitFullscreen]);

  // Handle escape key and body scroll
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isPlaying) {
        closeGame();
      }
    };

    if (isPlaying) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      
      // **NEW: Enhanced mobile viewport handling**
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        if (isMobile && (isLandscapeGaming || isFullscreen)) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
        } else {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
        }
      }
    } else {
      document.body.style.overflow = 'unset';
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isPlaying, closeGame, isMobile, isLandscapeGaming, isFullscreen]);

  // **COMPLETELY REWRITTEN: Enhanced Game Player with proper mobile landscape support**
  const GamePlayer = React.memo(() => {
    const [showRotateMessage, setShowRotateMessage] = useState(false);

    // **NEW: Toggle fullscreen specifically for game container**
    const toggleFullscreen = useCallback(() => {
      if (!isFullscreen) {
        enterFullscreenMode();
      } else {
        exitFullscreen();
      }
    }, [isFullscreen, enterFullscreenMode, exitFullscreen]);

    // **NEW: Show rotate message only when needed**
    useEffect(() => {
      const isPortrait = windowSize.height > windowSize.width && !orientation.includes('landscape');
      const shouldShowMessage = isMobile && isPortrait && !isLandscapeGaming && !isFullscreen;
      setShowRotateMessage(shouldShowMessage);
    }, [windowSize, orientation, isMobile, isLandscapeGaming, isFullscreen]);

    if (!selectedGame) return null;

    // **KEY FIX: Show only game iframe in landscape gaming mode**
    if (isLandscapeGaming && isMobile) {
      return (
        <div 
          id="game-fullscreen-container"
          className="fixed inset-0 bg-black z-50"
          style={{
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          {/* **NEW: Minimal floating controls for landscape gaming */}
          <div className="absolute top-2 right-2 z-50 flex space-x-2">
            <button 
              onClick={toggleFullscreen}
              className="bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-90 transition-all duration-300"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9h4.5M15 9V4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15h4.5M15 15v4.5m0 0l5.5 5.5" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
            
            <button 
              onClick={closeGame}
              className="bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-90 transition-all duration-300"
              title="Close Game"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* **NEW: Pure game iframe for landscape gaming */}
          <iframe 
            src={selectedGame.gameUrl}
            className="w-full h-full border-0"
            title={selectedGame.title}
            allowFullScreen
            allow="fullscreen; gamepad; microphone; camera; autoplay; gyroscope; accelerometer"
            sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation allow-popups allow-modals allow-presentation"
            style={{
              width: '100vw',
              height: '100vh',
              border: 'none',
              margin: 0,
              padding: 0,
            }}
          />
        </div>
      );
    }

    // **STANDARD: Regular game player for portrait/desktop**
    return (
      <div 
        id="game-fullscreen-container"
        className="fixed inset-0 bg-black z-50 flex flex-col"
        style={{
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {/* **NEW: Rotate message overlay */}
        {showRotateMessage && (
          <div className="absolute inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
            <div className="text-center text-white p-6 max-w-sm">
              <div className="text-6xl mb-4">📱🔄</div>
              <h3 className="text-xl font-bold mb-3">Rotate Your Device</h3>
              <p className="text-sm opacity-90 mb-4">
                For the best gaming experience, please rotate your device to landscape mode
              </p>
              <div className="flex flex-col space-y-3">
                <button 
                  onClick={toggleFullscreen}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Enter Fullscreen
                </button>
                <button 
                  onClick={closeGame}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close Game
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Top control bar */}
        <div className="flex justify-between items-center p-2 sm:p-4 bg-gray-900 text-white">
          <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
            <h3 className="text-sm sm:text-lg md:text-xl font-bold truncate">
              {selectedGame.title}
            </h3>
            <span className="hidden sm:inline-block px-2 sm:px-3 py-1 bg-blue-600 text-white text-xs sm:text-sm rounded-full whitespace-nowrap">
              {selectedGame.genre}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button 
              onClick={toggleFullscreen}
              className="text-white hover:text-blue-400 p-1 sm:p-2 rounded-full hover:bg-gray-700 transition-colors duration-300"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9h4.5M15 9V4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15h4.5M15 15v4.5m0 0l5.5 5.5" />
                </svg>
              ) : (
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>

            <button 
              onClick={closeGame}
              className="text-white hover:text-red-400 p-1 sm:p-2 rounded-full hover:bg-gray-700 transition-colors duration-300 ml-2"
              title="Close Game (Press ESC)"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Game iframe */}
        <div className="flex-1 relative bg-black overflow-hidden">
          <iframe 
            src={selectedGame.gameUrl}
            className="w-full h-full border-0"
            title={selectedGame.title}
            allowFullScreen
            allow="fullscreen; gamepad; microphone; camera; autoplay; gyroscope; accelerometer"
            sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation allow-popups allow-modals allow-presentation"
          />
        </div>
        
        {/* Bottom control bar */}
        <div className="p-2 sm:p-3 bg-gray-900 text-white text-xs sm:text-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-1 sm:space-y-0">
            <span className="text-gray-300 line-clamp-2 sm:line-clamp-1 flex-1">
              {selectedGame.shortDescription}
            </span>
            <span className="text-blue-300 text-xs whitespace-nowrap">
              <span className="hidden sm:inline">Press ESC to exit • Click icons for controls</span>
              <span className="sm:hidden">Tap controls to manage</span>
            </span>
          </div>
        </div>
      </div>
    );
  });

  // ConsentBanner extracted to SportsConsentBanner.js
  const ConsentBanner = () => <SportsConsentBanner showConsentBanner={showConsentBanner} onDecline={handleConsentDecline} onAccept={handleConsentAccept} />;

  // Clear search function
  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Reset filters function
  const resetFilters = useCallback(() => {
    setSelectedCategory('all');
    setSearchTerm('');
    setShowFavorites(false);
  }, []);

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* **NEW: Global styles for mobile landscape gaming */}
      {(isPlaying && isLandscapeGaming) && (
        <style jsx global>{`
          html, body {
            overflow: hidden !important;
            height: 100vh !important;
            width: 100vw !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          #root {
            height: 100vh !important;
            width: 100vw !important;
          }
          /* iOS Safari specific fixes */
          @supports (-webkit-touch-callout: none) {
            html, body {
              height: -webkit-fill-available !important;
            }
          }
        `}</style>
      )}

      {/* **NEW: Hide entire UI when in landscape gaming mode */}
      {!(isPlaying && isLandscapeGaming) && (
        <>
          {/* Header - Mobile Optimized with Centered Title */}
          <div className="bg-white shadow-sm sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
              <div className="flex items-center justify-between h-14 sm:h-16">
                {/* Left side - Back button */}
                <div className="flex items-center">
<Link
  to="/"
  className="inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all"
>
  <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
  <span className="sm:hidden">Back</span>
  <span className="hidden sm:inline">Back to Home</span>
</Link>

                </div>
                
                {/* Center - Title */}
                <div className="flex-1 flex justify-center">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-center">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      Games Collection
                    </span>
                  </h1>
                </div>
                
                {/* Right side - Controls */}
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <button 
                    onClick={toggleFavoritesView}
                    className={`relative p-1.5 sm:p-2 rounded-full transition-colors duration-300 ${
                      showFavorites || selectedCategory === 'favorites'
                        ? 'text-red-600 bg-red-50' 
                        : 'text-gray-600 hover:text-red-600'
                    }`}
                    title={showFavorites ? 'Show All Games' : 'Show Favorites'}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                    {favorites.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {favorites.length}
                      </span>
                    )}
                  </button>
                  
                  <button 
                    onClick={loadGames}
                    className="p-1.5 sm:p-2 text-gray-600 hover:text-blue-600 transition-colors duration-300"
                    title="Refresh Games"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
            {/* Instructions banner for tap and hold */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 text-center">
                💡 <strong>Tip:</strong> Tap and hold any game to add it to your favorites! ❤️
              </p>
            </div>

            {/* Search and Category Filter - Mobile Responsive */}
            <div className="mb-4 sm:mb-6 lg:mb-8">
              <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center gap-4">
                {/* Search Input */}
                <div className="relative flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder="Search games..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm text-sm sm:text-base"
                  />
                  <svg className="absolute left-3 sm:left-4 top-2.5 sm:top-3.5 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {searchTerm !== debouncedSearchTerm && (
                    <div className="absolute right-3 top-2.5 sm:top-3.5">
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
                
                {/* Category Dropdown */}
                <div className="relative">
                  <select
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    className="appearance-none bg-white border border-gray-200 rounded-full px-4 py-2.5 sm:py-3 pr-10 text-sm sm:text-base font-medium text-gray-700 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm cursor-pointer min-w-[140px] sm:min-w-[180px]"
                  >
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                  <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Show current view info */}
            {(showFavorites || selectedCategory === 'favorites') && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 text-center">
                  ❤️ Showing {favorites.length} favorite game{favorites.length !== 1 ? 's' : ''}
                  {favorites.length === 0 && ' - Start adding games to your favorites by tapping and holding!'}
                </p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-12 sm:py-16 lg:py-20">
                <div className="text-red-500 text-4xl sm:text-5xl lg:text-6xl mb-4">⚠️</div>
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">Oops! Something went wrong</h3>
                <p className="text-gray-600 mb-6 px-4">{error}</p>
                <button 
                  onClick={loadGames}
                  className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 text-sm sm:text-base"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Games Grid - Responsive Grid */}
            {!error && filteredGames.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-6">
                {filteredGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            )}

            {/* Empty State */}
            {!error && filteredGames.length === 0 && (
              <div className="text-center py-12 sm:py-16 lg:py-20 px-4">
                <div className="text-gray-400 text-6xl sm:text-7xl lg:text-8xl mb-6">
                  {showFavorites || selectedCategory === 'favorites' ? '❤️' : '🔍'}
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">
                  {showFavorites || selectedCategory === 'favorites' ? 'No favorites yet' : 'No games found'}
                </h3>
                <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base lg:text-lg max-w-md mx-auto">
                  {showFavorites || selectedCategory === 'favorites'
                    ? 'Tap and hold on any game to add it to your favorites! Your favorites are saved automatically.'
                    : searchTerm 
                      ? `No games match "${searchTerm}". Try a different search term.`
                      : 'Try selecting a different category or refresh the page.'
                  }
                </p>
                <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                  {searchTerm && (
                    <button 
                      onClick={clearSearch}
                      className="bg-gray-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-gray-700 transition-colors duration-300 text-sm sm:text-base"
                    >
                      Clear Search
                    </button>
                  )}
                  <button 
                    onClick={resetFilters}
                    className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 text-sm sm:text-base"
                  >
                    Show All Games
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Floating Action Buttons - Mobile Optimized */}
          <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 flex flex-col space-y-2 sm:space-y-3">
            <button
              onClick={scrollToTop}
              className="bg-gray-700 text-white p-2.5 sm:p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
              title="Scroll to top"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </button>
            
            <Link 
              to="/"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-2 sm:p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
              title="Back"
            >
              <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </Link>
          </div>
        </>
      )}

      {/* Full Screen Game Player - Always rendered when playing */}
      {isPlaying && <GamePlayer />}

      {/* GDPR Consent Banner - Only show when not in landscape gaming */}
      {!(isPlaying && isLandscapeGaming) && <ConsentBanner />}
    </div>
  );
};

export default SportsGames;
